/**
 * New Hires / Onboarding Tracker Component
 * Excel-like grid for managing onboarding records
 */

import React, { useState, useEffect, useCallback } from "react";
import onboardingService from "../shared/services/onboardingService";
import EditableCell from "../shared/components/EditableCell";
import OnboardingForm from "./components/OnboardingForm";
import { ONBOARDING_COLUMNS } from "./utils/onboardingColumns";
import "./styles/NewHires.css";

function NewHires() {
  const [onboardingRecords, setOnboardingRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Load onboarding records on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await onboardingService.getAllOnboarding();
      setOnboardingRecords(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Error loading onboarding records:", err);
      setError("Failed to load onboarding records. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Excel-like column filtering and sorting
  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...onboardingRecords];

    // Apply column filters
    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (filterValue && filterValue.trim() !== '') {
        filtered = filtered.filter(record => {
          const cellValue = record[columnKey];
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Compare values
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredRecords(filtered);
  }, [onboardingRecords, columnFilters, sortConfig]);

  // Apply filters and sorting whenever records, columnFilters, or sortConfig change
  useEffect(() => {
    applyFiltersAndSort();
  }, [onboardingRecords, columnFilters, sortConfig, applyFiltersAndSort]);

  const handleColumnFilterChange = (columnKey, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setSortConfig({ key: null, direction: 'asc' });
  };

  // Handle inline cell update
  const handleCellUpdate = async (recordId, field, value) => {
    try {
      // Check if field is readonly
      const column = ONBOARDING_COLUMNS.find(col => col.key === field);
      if (column?.readOnly) {
        setError("This field is read-only and cannot be edited manually.");
        setTimeout(() => setError(null), 3000);
        return;
      }

      // Optimistically update UI
      setOnboardingRecords((prev) =>
        prev.map((r) => (r.id === recordId ? { ...r, [field]: value } : r))
      );

      // Send update to server
      await onboardingService.updateOnboarding(recordId, { [field]: value });
    } catch (err) {
      console.error("Error updating onboarding record:", err);
      setError("Failed to update onboarding record. Please try again.");
      // Reload data to revert optimistic update
      loadData();
    }
  };

  // Handle opening form for new record
  const handleAddNew = () => {
    setEditingRecord(null);
    setShowForm(true);
  };

  // Handle opening form for editing
  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  // Handle form save
  const handleFormSave = async (recordData) => {
    try {
      if (editingRecord) {
        await onboardingService.updateOnboarding(
          editingRecord.id,
          recordData
        );
      } else {
        await onboardingService.createOnboarding(recordData);
      }
      await loadData();
      setShowForm(false);
      setEditingRecord(null);
    } catch (err) {
      console.error("Error saving onboarding record:", err);
      throw err;
    }
  };

  // Handle delete
  const handleDelete = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this onboarding record?")) {
      return;
    }

    try {
      await onboardingService.deleteOnboarding(recordId);
      await loadData();
    } catch (err) {
      console.error("Error deleting onboarding record:", err);
      setError("Failed to delete onboarding record. Please try again.");
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="new-hires-loading">
        <div className="loading-spinner"></div>
        <p>Loading onboarding records...</p>
      </div>
    );
  }

  return (
    <div className="new-hires">
      {/* Header */}
      <div className="tracker-header">
        <h1>New Hires / Onboarding Tracker</h1>
        <div className="tracker-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            + Add New Hire
          </button>
          <button className="btn-secondary" onClick={handleRefresh}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Excel-like Grid */}
      <div className="tracker-grid-container">
        <table className="tracker-grid">
          <thead>
            <tr>
              <th className="action-column" rowSpan="2">
                Actions
                {Object.keys(columnFilters).length > 0 && (
                  <button
                    className="clear-filters-btn"
                    onClick={clearAllFilters}
                    title="Clear all filters and sorting"
                  >
                    ✕
                  </button>
                )}
              </th>
              {ONBOARDING_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ minWidth: col.width, cursor: 'pointer' }}
                  onClick={() => handleSort(col.key)}
                  title={`Click to sort by ${col.label}`}
                >
                  <div className="column-header">
                    <span>
                      {col.label}
                      {col.required && <span className="required">*</span>}
                      {col.readOnly && <span className="readonly-badge">🔒</span>}
                    </span>
                    <span className="sort-indicator">
                      {sortConfig.key === col.key && (
                        sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
            <tr className="filter-row">
              {ONBOARDING_COLUMNS.map((col) => (
                <th key={`filter-${col.key}`} className="filter-cell">
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder={`Filter ${col.label}...`}
                    value={columnFilters[col.key] || ''}
                    onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan={ONBOARDING_COLUMNS.length + 1} className="no-data">
                  {onboardingRecords.length === 0
                    ? "No onboarding records found. Click '+ Add New Hire' to get started."
                    : "No records match your filters."}
                </td>
              </tr>
            ) : (
              filteredRecords.map((record) => (
                <tr key={record.id}>
                  <td className="action-column">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(record)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(record.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                  {ONBOARDING_COLUMNS.map((col) => (
                    <EditableCell
                      key={`${record.id}-${col.key}`}
                      value={record[col.key]}
                      type={col.type}
                      options={col.options}
                      readOnly={col.readOnly}
                      onSave={(newValue) =>
                        handleCellUpdate(record.id, col.key, newValue)
                      }
                    />
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <OnboardingForm
          onboarding={editingRecord}
          onSave={handleFormSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
        />
      )}
    </div>
  );
}

export default NewHires;